import { inject, Injectable } from '@angular/core';
import { MapMarker, MarkerClusterContentTable } from '@features/map/interfaces';
import { POIDto } from '@pages/poi-page/interfaces';
import { TranslateService } from '@ngx-translate/core';
import RouteNetworkColorUtils from '@pages/route-network-page/utils/route-network-color.utils';
import { MarkerType } from '@features/map/constants/constants';
import MarkerPopupUtils from '@features/map/utils/marker-popup.utils';
import { UserMetadataService } from '@core/services/user-metadata.service';
import GeneralUtils from '@shared/utils/general.utils';

@Injectable({ providedIn: 'root' })
export class TypedPOIService {
  private translate = inject(TranslateService);
  private metaDataService = inject(UserMetadataService);
  getTypedPOIMarkers(
    data: Partial<POIDto>[],
    enableMarkerEdit?: boolean
  ): MapMarker[] {
    const isSkiDomainEnabled = this.metaDataService.hasSkiDomain();
    return data
      .filter(
        poi =>
          GeneralUtils.isValueANumber(poi.GPSLat) &&
          GeneralUtils.isValueANumber(poi.GPSLon)
      )
      .map(poi => {
        const marker: MapMarker = {
          lat: poi.GPSLat || 0,
          lng: poi.GPSLon || 0,
          options: {
            riseOnHover: true,
            isToolTipPermanent: false,
            tooltipValue:
              poi.Description || poi.Address || poi.PostalNumber
                ? this.getPoiMarkerTooltipDescription(
                    poi.Description,
                    poi.Address,
                    poi.PostalNumber
                  )
                : '',
            popup: {
              customElement: isSkiDomainEnabled
                ? MarkerPopupUtils.getPoiPopupElementFromRegistry(poi)
                : undefined,
              popupTableData: this.constructClusterPopupTable(
                poi,
                isSkiDomainEnabled
              ),
            },
          },
          extraData: { editable: !!enableMarkerEdit },
        } satisfies MapMarker;

        if (poi.IconKey) {
          marker.options!.customMarker = {
            markerType: MarkerType.Poi,
            iconUrl: '',
            iconHtml: RouteNetworkColorUtils.getIconWithFill(
              poi.IconKey || 'icon-info',
              poi.IconColor || 'black'
            ),
            iconSize: [48, 48],
            className: 'poi-marker',
          };
        }

        return marker;
      });
  }

  private getPoiMarkerTooltipDescription(
    description?: string | null,
    address?: string | null,
    postalNumber?: string | null
  ): string {
    const parts: string[] = [];

    // Add description, with ":" only if others exist
    if (description) {
      const hasMore = !!(address || postalNumber);
      parts.push(hasMore ? `${description}:` : description);
    }

    if (address) parts.push(address);
    if (postalNumber) parts.push(postalNumber);

    return parts.join(', ');
  }

  private constructClusterPopupTable(
    poi: Partial<POIDto>,
    isSkiDomainEnabled = false
  ): MarkerClusterContentTable {
    const popupData: { [header: string]: string } = {};
    popupData[this.translate.instant('POI.Description')] =
      poi.Description || '';
    if (!isSkiDomainEnabled) {
      popupData[this.translate.instant('POI.Address')] = poi.Address || '';
      popupData[this.translate.instant('POI.PostalNumber')] =
        poi.PostalNumber || '';
      popupData[this.translate.instant('POI.PostalPlace')] =
        poi.PostalPlace || '';
    }
    return {
      contentData: popupData,
    };
  }
}
